import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Mail, Phone, Building2, MapPin, Inbox, Calendar, Search, FileText, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND, MODULES } from "@/lib/constants";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      setBusy(true);
      try {
        const [c, d, b] = await Promise.all([
          api.get("/contact", { withCredentials: true }),
          api.get("/demo-request", { withCredentials: true }),
          api.get("/brochure-download", { withCredentials: true }),
        ]);
        if (!active) return;
        setContacts(c.data);
        setDemos(d.data);
        setBrochures(b.data);
        setErr(null);
      } catch (e) {
        if (!active) return;
        setErr(e?.response?.data?.detail || "Failed to load leads");
      } finally {
        if (active) setBusy(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="animate-spin text-[#E8772E]" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const q = query.trim().toLowerCase();
  const filterContact = (c) =>
    !q ||
    [c.name, c.email, c.company, c.message].filter(Boolean).join(" ").toLowerCase().includes(q);
  const filterDemo = (d) =>
    !q ||
    [d.name, d.email, d.company, d.country, d.message, ...(d.modules || [])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
  const filterBrochure = (b) =>
    !q ||
    [b.name, b.email, b.company].filter(Boolean).join(" ").toLowerCase().includes(q);

  const filteredContacts = contacts.filter(filterContact);
  const filteredDemos = demos.filter(filterDemo);
  const filteredBrochures = brochures.filter(filterBrochure);

  return (
    <div className="flex min-h-screen flex-col bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 border-b border-[#E2E8F0] bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-home-link" className="flex items-center gap-3">
              <img src={BRAND.logos.primecore} alt="PrimeCore" className="h-8 w-auto" />
            </Link>
            <span className="hidden h-5 w-px bg-[#E2E8F0] sm:inline-block" />
            <span className="hidden text-xs font-semibold uppercase tracking-[0.18em] text-[#475569] sm:inline-block">
              Admin Console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 text-xs text-[#475569] sm:flex">
              {user.picture && (
                <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full" />
              )}
              <span data-testid="admin-user-email" className="font-medium text-[#0A1128]">{user.email}</span>
            </div>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-medium text-[#0A1128] hover:border-[#0A1128]"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
              Welcome back
            </p>
            <h1 className="mt-2 font-serif-display text-3xl text-[#0A1128] sm:text-4xl">
              {user.name?.split(" ")[0] || "Admin"} — your leads inbox
            </h1>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Demo Requests" value={demos.length} icon={Calendar} />
            <StatCard label="Contact Messages" value={contacts.length} icon={Inbox} />
            <StatCard label="Brochure Downloads" value={brochures.length} icon={Download} />
          </div>
        </div>

        {err && (
          <div data-testid="admin-error" className="mt-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {err}
          </div>
        )}

        <div className="mt-8">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
            <Input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, company..."
              className="pl-9"
            />
          </div>
        </div>

        <Tabs defaultValue="demos" className="mt-8">
          <TabsList className="bg-[#F1F5F9]">
            <TabsTrigger data-testid="admin-tab-demos" value="demos">
              Demo Requests ({filteredDemos.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-contacts" value="contacts">
              Contact Messages ({filteredContacts.length})
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-brochures" value="brochures">
              Brochure Downloads ({filteredBrochures.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="demos" className="mt-6">
            {busy ? (
              <Loading />
            ) : filteredDemos.length === 0 ? (
              <Empty label="No demo requests yet." />
            ) : (
              <ul className="space-y-4">
                {filteredDemos.map((d) => (
                  <DemoCard key={d.id} d={d} />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            {busy ? (
              <Loading />
            ) : filteredContacts.length === 0 ? (
              <Empty label="No contact messages yet." />
            ) : (
              <ul className="space-y-4">
                {filteredContacts.map((c) => (
                  <ContactCard key={c.id} c={c} />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="brochures" className="mt-6">
            {busy ? (
              <Loading />
            ) : filteredBrochures.length === 0 ? (
              <Empty label="No brochure downloads yet." />
            ) : (
              <ul className="space-y-4">
                {filteredBrochures.map((b) => (
                  <BrochureCard key={b.id} b={b} />
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-5 py-4">
      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#0A1128] text-white">
        <Icon size={18} />
      </span>
      <div>
        <p className="text-xs uppercase tracking-[0.16em] text-[#64748B]">{label}</p>
        <p className="font-serif-display text-2xl text-[#0A1128]">{value}</p>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="animate-spin text-[#E8772E]" size={24} />
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-white p-12 text-center text-sm text-[#64748B]">
      {label}
    </div>
  );
}

function formatDate(s) {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function moduleNames(ids) {
  const map = Object.fromEntries(MODULES.map((m) => [m.id, m.name]));
  return (ids || []).map((id) => map[id] || id);
}

function DemoCard({ d }) {
  return (
    <li data-testid={`demo-row-${d.id}`} className="rounded-lg border border-[#E2E8F0] bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif-display text-xl text-[#0A1128]">{d.name}</p>
          <p className="text-xs uppercase tracking-[0.14em] text-[#E8772E]">
            {d.role || "—"} · {d.country}
          </p>
        </div>
        <p className="text-xs text-[#64748B]">{formatDate(d.created_at)}</p>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-[#334155] sm:grid-cols-2">
        <span className="flex items-center gap-2"><Mail size={14} className="text-[#475569]" />{d.email}</span>
        <span className="flex items-center gap-2"><Building2 size={14} className="text-[#475569]" />{d.company}</span>
      </div>
      {d.modules?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {moduleNames(d.modules).map((n) => (
            <span key={n} className="rounded-full bg-[#FFF7F0] px-3 py-1 text-xs font-medium text-[#D66826]">
              {n}
            </span>
          ))}
        </div>
      )}
      {d.message && (
        <p className="mt-4 whitespace-pre-wrap rounded-md bg-[#F8FAFC] p-4 text-sm leading-relaxed text-[#334155]">
          {d.message}
        </p>
      )}
    </li>
  );
}

function ContactCard({ c }) {
  return (
    <li data-testid={`contact-row-${c.id}`} className="rounded-lg border border-[#E2E8F0] bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif-display text-xl text-[#0A1128]">{c.name}</p>
          <p className="text-xs uppercase tracking-[0.14em] text-[#E8772E]">
            {c.company || "—"}
          </p>
        </div>
        <p className="text-xs text-[#64748B]">{formatDate(c.created_at)}</p>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-[#334155] sm:grid-cols-2">
        <span className="flex items-center gap-2"><Mail size={14} className="text-[#475569]" />{c.email}</span>
        {c.phone && <span className="flex items-center gap-2"><Phone size={14} className="text-[#475569]" />{c.phone}</span>}
      </div>
      <p className="mt-4 whitespace-pre-wrap rounded-md bg-[#F8FAFC] p-4 text-sm leading-relaxed text-[#334155]">
        {c.message}
      </p>
    </li>
  );
}

function BrochureCard({ b }) {
  return (
    <li data-testid={`brochure-row-${b.id}`} className="rounded-lg border border-[#E2E8F0] bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#FFF7F0] text-[#E8772E]">
            <FileText size={18} />
          </span>
          <div>
            <p className="font-serif-display text-xl text-[#0A1128]">{b.name}</p>
            <p className="text-xs uppercase tracking-[0.14em] text-[#E8772E]">{b.company}</p>
          </div>
        </div>
        <p className="text-xs text-[#64748B]">{formatDate(b.created_at)}</p>
      </div>
      <div className="mt-4 text-sm text-[#334155]">
        <a href={`mailto:${b.email}`} className="inline-flex items-center gap-2 hover:text-[#E8772E]">
          <Mail size={14} className="text-[#475569]" />{b.email}
        </a>
      </div>
    </li>
  );
}
